import React, { useEffect, useRef, useState } from 'react';
import { BaseEditModal } from '../base-edit-modal';
import { DeleteIcon } from 'assets';

export interface DetailsFormData {
  assessmentTitle: string;
  aboutRole: string;
  responsibilities: string[];
  qualifications: string[];
}

interface DetailsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTitle: string;
  currentAboutRole: string;
  currentResponsibilities: string[];
  currentQualifications: string[];
  onSave: (data: DetailsFormData) => void;
  isLoading?: boolean;
}

const labelClass = 'text-[#667085] text-base font-normal';
const deleteBtnClass =
  'bg-transparent border-none p-0 cursor-pointer inline-flex items-center [&_svg]:w-[18px] [&_svg]:h-[18px]';
const boxClass =
  'w-full border border-[#D0D5DD] rounded-[10px] bg-white focus-within:border-[var(--primary-blue)]';

// A bulleted list where every line is prefixed with a bullet. Pressing Enter
// starts a new bullet on the next line so the recruiter can keep listing items,
// and clearing a line's text (then Backspace) removes that bullet.
const BulletListEditor: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
}> = ({ items, onChange }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusIndex = useRef<number | null>(null);

  useEffect(() => {
    if (focusIndex.current !== null) {
      const el = inputRefs.current[focusIndex.current];
      if (el) {
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
      focusIndex.current = null;
    }
  });

  const updateItem = (index: number, value: string) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const next = [...items];
      next.splice(index + 1, 0, '');
      focusIndex.current = index + 1;
      onChange(next);
    } else if (e.key === 'Backspace' && items[index] === '' && items.length > 1) {
      e.preventDefault();
      const next = [...items];
      next.splice(index, 1);
      focusIndex.current = Math.max(0, index - 1);
      onChange(next);
    }
  };

  return (
    <div className={`${boxClass} flex flex-col gap-0 px-4 py-2`}>
      {items.map((item, index) => (
        <div className="flex items-center gap-1" key={index}>
          <span className="text-[#1D2939] text-base leading-none shrink-0">&bull;</span>
          <input
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            className="flex-1 border-none outline-none text-base text-[#1D2939] bg-transparent py-1"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          />
        </div>
      ))}
    </div>
  );
};

const withFallback = (arr?: string[]): string[] =>
  arr && arr.length > 0 ? [...arr] : [''];

const cleanList = (arr: string[]): string[] =>
  arr.map((line) => line.trim()).filter(Boolean);

export const DetailsEditModal: React.FC<DetailsEditModalProps> = ({
  isOpen,
  onClose,
  currentTitle,
  currentAboutRole,
  currentResponsibilities,
  currentQualifications,
  onSave,
  isLoading,
}) => {
  const [title, setTitle] = useState('');
  const [aboutRole, setAboutRole] = useState('');
  const [responsibilities, setResponsibilities] = useState<string[]>(['']);
  const [qualifications, setQualifications] = useState<string[]>(['']);

  // Section visibility — the trash icon deletes a whole section from the details.
  const [showAboutRole, setShowAboutRole] = useState(true);
  const [showResponsibilities, setShowResponsibilities] = useState(true);
  const [showQualifications, setShowQualifications] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(currentTitle ?? '');
    setAboutRole(currentAboutRole ?? '');
    setResponsibilities(withFallback(currentResponsibilities));
    setQualifications(withFallback(currentQualifications));
    setShowAboutRole(true);
    setShowResponsibilities(true);
    setShowQualifications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onSave({
      assessmentTitle: title.trim(),
      aboutRole: showAboutRole ? aboutRole.trim() : '',
      responsibilities: showResponsibilities ? cleanList(responsibilities) : [],
      qualifications: showQualifications ? cleanList(qualifications) : [],
    });
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editing Details"
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      cancelButtonText=""
      saveButtonText="Update"
      contentClassName="!bg-white"
    >
      <div className="font-[Gelion] w-[88%] mx-auto py-[22px] flex flex-col gap-4">
        <div className="flex flex-col gap-2.5">
          <span className={labelClass}>Assessment title</span>
          <input
            className={`${boxClass} focus:border-[var(--primary-blue)] px-4 py-3.5 text-base text-[#1D2939]`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {showAboutRole && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <span className={labelClass}>About the role</span>
              <button
                type="button"
                className={deleteBtnClass}
                aria-label="Delete about the role"
                onClick={() => setShowAboutRole(false)}
              >
                <DeleteIcon />
              </button>
            </div>
            <textarea
              className={`${boxClass} focus:border-[var(--primary-blue)] px-4 py-3.5 min-h-[120px] text-base leading-normal text-[#1D2939] resize-y`}
              value={aboutRole}
              onChange={(e) => setAboutRole(e.target.value)}
            />
          </div>
        )}

        {showResponsibilities && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <span className={labelClass}>Responsibilities</span>
              <button
                type="button"
                className={deleteBtnClass}
                aria-label="Delete responsibilities"
                onClick={() => setShowResponsibilities(false)}
              >
                <DeleteIcon />
              </button>
            </div>
            <BulletListEditor items={responsibilities} onChange={setResponsibilities} />
          </div>
        )}

        {showQualifications && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <span className={labelClass}>What we&rsquo;re looking for/ Qualification</span>
              <button
                type="button"
                className={deleteBtnClass}
                aria-label="Delete qualifications"
                onClick={() => setShowQualifications(false)}
              >
                <DeleteIcon />
              </button>
            </div>
            <BulletListEditor items={qualifications} onChange={setQualifications} />
          </div>
        )}
      </div>
    </BaseEditModal>
  );
};
